/**
 * homePage.tsx — Landing fusionnée Earniverse (Home + Get Started), DA V2 "bold editorial".
 *
 * COMPOSE les styled-components de ./homePage.styles.ts + les primitives DA V2
 * (Eyebrow, SectionHead, SectionTitle, SectionBar, SectionCount) de ../../components/ui/daV2.
 * Tokens couleur/font/radius/gradient : centralisés dans theme.js (§20, via les styles).
 *
 * Rendu cible 1:1 (§43) = build/previews/dav2-fusion-B.html.
 * Surface UIX (§39) = src/components/ui/preview/mocks/landing-fusion.mock.jsx (clé `landing-fusion`).
 *
 * 7 sections : Hero · HUD · Features · Steps · Download · Earn · Gallery.
 * Header sticky DA + Footer sombre réutilisés (jamais réinventés, §18).
 */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { readStoredUser } from '../../utils/auth/userStorage';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as actions from '../../store/actions/thunks';
import { EPATHS } from '../../routes/paths.enum';

import { StyledHeader } from '../MarkePlace/Styles';
import Header from '../MarkePlace/menu/header';
import Footer from '../MarkePlace/components/footer';

import { SectionBar, SectionCount } from '../../components/ui/daV2';

import EIV_COVER_1 from '../../assets/images/EIV_COVER_1.jpg';
import VR_HEADSET from '../../assets/images/VR_HEADSET.png';
import CITY_PNG from '../../assets/images/CITY_PNG.png';
import EIV_BACK_COVER_5 from '../../assets/images/EIV_BACK_COVER_5.jpg';
import EPIC_LOGO from '../../assets/images/epic_logo.jpg';
import IMAGE_FRAME_4 from '../../assets/images/Image_Frame4.jpg';
import COLLECTIBLES_CAR from '../../assets/images/collectibles-car.png';

import {
  Wrap,
  BtnPrimary,
  BtnOutline,
  BtnSpecs,
  LinkReset,
  Hero,
  HeroTitle,
  HeroLead,
  CtaRow,
  HeroMedia,
  HeroCorner,
  HeroBadge,
  HudGrid,
  HudBox,
  HudKey,
  HudVal,
  FGrid,
  FCard,
  FCorner,
  FThumb,
  FBody,
  FEyebrow,
  FTitle,
  FText,
  FBtn,
  Steps,
  Step,
  StepCorner,
  StepN,
  StepTitle,
  StepText,
  DlCard,
  DlEpic,
  DlEpicTag,
  DlTitle,
  DlText,
  DlNote,
  DlBtns,
  Earn,
  EarnMedia,
  EarnCopy,
  EarnTitle,
  EarnBar,
  Gallery,
  HeroEyebrow,
  LSectionHead,
  LSectionTitle,
} from './homePage.styles';

// HeroEyebrow = override landing (.72rem) de la primitive daV2 Eyebrow. daV2.jsx étant en JS,
// alias typé pour exposer la prop transient `$bright` côté .tsx sans toucher la primitive (§18).
const Eyebrow = HeroEyebrow as React.FC<React.PropsWithChildren<{ $bright?: boolean; $lg?: boolean }>>;

// SWITCH VARIABLE FOR PAGE STYLE (parité avec l'ancien homePage : navbar HOME)
const theme = 'HOME'; // LIGHT, GREY, RETRO

// Liens externes (téléchargement Epic + specs)
const DOWNLOAD_URL = 'https://earniverse.io/download-epic';
const SPECS_URL = 'https://wiki.earniverse.io/docs/technical-requirements/';

function HomePage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Normalisation défensive : si aucune session persistée, s'assurer que le store
    // reflète un visiteur (le boot hydrate déjà depuis userStorage, ceci couvre le cas
    // d'un _user effacé hors de l'onglet). Lecture via userStorage (source unique).
    if (!readStoredUser()) {
      dispatch(actions.setUser({}));
    }
  }, [dispatch]);

  return (
    <div>
      <StyledHeader theme={theme} />
      <Header className="HOME" />

      <Wrap>
        {/* ─── 1. HERO ─────────────────────────────────────────────────────────── */}
        <Hero>
          <div>
            <Eyebrow $lg>{t('HomeHeroEyebrow')}</Eyebrow>
            <HeroTitle>
              {t('HomeHeroPlayPrefix')} <span className="hl">Earniverse</span>
            </HeroTitle>
            <HeroLead>{t('HomeHeroLead')}</HeroLead>
            <CtaRow>
              <LinkReset href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                <BtnPrimary>{t('HomeDownloadEarniverse')}</BtnPrimary>
              </LinkReset>
              <Link to={EPATHS.EXPLORE} style={{ textDecoration: 'none' }}>
                <BtnOutline>{t('HomeVisitMarketplace')}</BtnOutline>
              </Link>
            </CtaRow>
          </div>

          <HeroMedia>
            <HeroCorner />
            <HeroBadge>{t('HomeHeroBadge')}</HeroBadge>
            <img src={EIV_COVER_1} alt="Earniverse VR metaverse cover" />
          </HeroMedia>
        </Hero>

        {/* ─── 2. HUD ──────────────────────────────────────────────────────────── */}
        <HudGrid>
          <HudBox>
            <HudKey>{t('HomeHudOpenWorld')}</HudKey>
            <HudVal>{t('HomeHudOpenWorldVal')}</HudVal>
          </HudBox>
          <HudBox>
            <HudKey>{t('HomeHudOwnership')}</HudKey>
            <HudVal>{t('HomeHudOwnershipVal')}</HudVal>
          </HudBox>
          <HudBox>
            <HudKey>{t('HomeHudCollectibles')}</HudKey>
            <HudVal>{t('HomeHudCollectiblesVal')}</HudVal>
          </HudBox>
        </HudGrid>

        {/* ─── 3. FEATURES ─────────────────────────────────────────────────────── */}
        <LSectionHead>
          <LSectionTitle>{t('HomeWhatYouCanDo')}</LSectionTitle>
          <SectionBar />
          <SectionCount>{t('HomeWorldsCount')}</SectionCount>
        </LSectionHead>

        <FGrid>
          {/* Explore */}
          <FCard>
            <FCorner />
            <FThumb $contain>
              <img src={VR_HEADSET} loading="lazy" alt="VR headset" />
            </FThumb>
            <FBody>
              <FEyebrow>{t('HomeFeatureDiscover')}</FEyebrow>
              <FTitle>{t('HomeFeatureExplore')}</FTitle>
              <FText>{t('HomeFeatureExploreText')}</FText>
              <FBtn as={Link as any} to={EPATHS.HOME}>
                {t('HomeGetStarted')}
              </FBtn>
            </FBody>
          </FCard>

          {/* Create */}
          <FCard>
            <FCorner />
            <FThumb $contain>
              <img src={CITY_PNG} loading="lazy" alt="Earniverse 3D city" />
            </FThumb>
            <FBody>
              <FEyebrow>{t('HomeFeatureBuild')}</FEyebrow>
              <FTitle>{t('HomeFeatureCreate')}</FTitle>
              <FText>{t('HomeFeatureCreateText')}</FText>
              <FBtn as={Link as any} to={EPATHS.HOME}>
                {t('HomeGetStarted')}
              </FBtn>
            </FBody>
          </FCard>

          {/* Trade */}
          <FCard $trade>
            <FCorner $trade />
            <FThumb>
              <img src={EIV_BACK_COVER_5} loading="lazy" alt="Earniverse collectibles trading" />
            </FThumb>
            <FBody>
              <FEyebrow $cta>{t('HomeFeatureEarn')}</FEyebrow>
              <FTitle>{t('HomeFeatureTrade')}</FTitle>
              <FText>{t('HomeFeatureTradeText')}</FText>
              <FBtn $cta as={Link as any} to={EPATHS.EXPLORE}>
                {t('HomeVisitMarketplace')}
              </FBtn>
            </FBody>
          </FCard>
        </FGrid>

        {/* ─── 4. STEPS ────────────────────────────────────────────────────────── */}
        <LSectionHead>
          <LSectionTitle>{t('HomeHowItWorks')}</LSectionTitle>
          <SectionBar />
          <SectionCount>{t('HomeStepsCount')}</SectionCount>
        </LSectionHead>

        <Steps>
          <Step>
            <StepCorner />
            <StepN>{t('HomeStep01')}</StepN>
            <StepTitle>{t('HomeStep1Title')}</StepTitle>
            <StepText>{t('HomeStep1Text')}</StepText>
          </Step>
          <Step>
            <StepCorner />
            <StepN>{t('HomeStep02')}</StepN>
            <StepTitle>{t('HomeStep2Title')}</StepTitle>
            <StepText>{t('HomeStep2Text')}</StepText>
          </Step>
          <Step>
            <StepCorner />
            <StepN>{t('HomeStep03')}</StepN>
            <StepTitle>{t('HomeStep3Title')}</StepTitle>
            <StepText>{t('HomeStep3Text')}</StepText>
          </Step>
        </Steps>

        {/* ─── 5. DOWNLOAD ─────────────────────────────────────────────────────── */}
        <LSectionHead>
          <LSectionTitle>{t('HomeNowAvailable')}</LSectionTitle>
          <SectionBar />
        </LSectionHead>

        <DlCard>
          <DlEpic>
            <img src={EPIC_LOGO} loading="lazy" alt="Epic Games" />
            <DlEpicTag>{t('HomeEpicGames')}</DlEpicTag>
          </DlEpic>
          <div>
            <DlTitle>{t('HomeDownloadEarniverse')}</DlTitle>
            <DlText>{t('HomeDlText')}</DlText>
            <DlNote>{t('HomeEpicAccountRequired')}</DlNote>
            <DlBtns>
              <LinkReset href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                <BtnPrimary>{t('HomeDownloadEarniverse')}</BtnPrimary>
              </LinkReset>
              <LinkReset href={SPECS_URL} target="_blank" rel="noopener noreferrer">
                <BtnSpecs>{t('HomeViewSpecs')}</BtnSpecs>
              </LinkReset>
            </DlBtns>
          </div>
        </DlCard>

        {/* ─── 6. EARN ─────────────────────────────────────────────────────────── */}
        <Earn>
          <EarnMedia>
            <img src={COLLECTIBLES_CAR} loading="lazy" alt="Earniverse 3D collectible vehicle" />
          </EarnMedia>
          <EarnCopy>
            <Eyebrow $bright>{t('HomeOwnYourWorld')}</Eyebrow>
            <EarnTitle>{t('HomeEarnTitle')}</EarnTitle>
            <EarnBar />
            <Link to={EPATHS.EXPLORE} style={{ textDecoration: 'none' }}>
              <BtnPrimary>{t('HomeVisitMarketplace')}</BtnPrimary>
            </Link>
          </EarnCopy>
        </Earn>

        {/* ─── 7. GALLERY ──────────────────────────────────────────────────────── */}
        <Gallery>
          <img src={EIV_BACK_COVER_5} loading="lazy" alt="Earniverse gallery 1" />
          <img src={IMAGE_FRAME_4} loading="lazy" alt="Earniverse gallery 2" />
        </Gallery>
      </Wrap>

      <Footer />
    </div>
  );
}

export default HomePage;
